import React, { useState } from 'react';
import { SlideFooter } from '../components/canvas/SlideFooter';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, KICKER_FONT_SIZE_LEVELS, DEFAULT_KICKER_FONT_SIZE, SUBTITLE_FONT_SIZE_LEVELS, DEFAULT_SUBTITLE_FONT_SIZE, STATS_FONT_SIZE_LEVELS, DEFAULT_STATS_FONT_SIZE, TAGS_FONT_SIZE_LEVELS, DEFAULT_TAGS_FONT_SIZE } from '../constants/typography';
import { usePreviewInteraction } from '../contexts/PreviewInteractionContext';
import { InlineEditable } from '../components/canvas/InlineEditable';
import { NudgeableGroup } from '../components/canvas/NudgeableGroup';

/**
 * Normies Dashboard layout — Market stats / daily recap template.
 *
 * Bold stat grid with LCD-style readouts. Fills the 1080×1350 canvas.
 * Hardware instrument aesthetic — large numbers, clear hierarchy.
 */

function DashboardStat({ label, value, pc, size = 'normal', fontSize }) {
  const isLarge = size === 'large';
  // Scale proportionally from the slider value (default 20px = 1.0×)
  const scale = fontSize ? fontSize / 20 : 1;
  const valuePx = Math.round((isLarge ? 40 : 28) * scale);
  const labelPx = Math.round((isLarge ? 13 : 11) * scale);
  const padV = Math.round((isLarge ? 24 : 18) * scale);
  const padH = Math.round((isLarge ? 24 : 20) * scale);
  return (
    <div style={{
      background: 'rgba(0,0,0,0.40)',
      border: `1px solid ${pc.accent(0.18)}`,
      padding: `${padV}px ${padH}px`,
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    }}>
      <div style={{
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: `${labelPx}px`,
        fontWeight: 700,
        letterSpacing: '0.18em',
        color: pc.textSubtle,
        textTransform: 'uppercase',
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: `${valuePx}px`,
        fontWeight: 700,
        color: pc.el.title || pc.textPrimary,
        letterSpacing: '-0.02em',
        lineHeight: 1.05,
      }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

export function NormiesDashboardLayout({ data, fontPreset, pc }) {
  const { interactive, onElementClick, editingField, onEditChange, onEndEdit } = usePreviewInteraction();
  const [imgLoaded, setImgLoaded] = useState(false);

  const titleSize = TITLE_FONT_SIZE_LEVELS[data.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE]?.size || 74;
  const kickerSize = KICKER_FONT_SIZE_LEVELS[data.kickerFontSize ?? DEFAULT_KICKER_FONT_SIZE]?.size || 16;
  const subtitleSize = SUBTITLE_FONT_SIZE_LEVELS[data.subtitleFontSize ?? DEFAULT_SUBTITLE_FONT_SIZE]?.size || 20;
  const tagsSize = TAGS_FONT_SIZE_LEVELS[data.tagsFontSize ?? DEFAULT_TAGS_FONT_SIZE]?.size || 11;
  const statsSize = STATS_FONT_SIZE_LEVELS[data.statsFontSize ?? DEFAULT_STATS_FONT_SIZE]?.size || 20;

  // "None" = CTA text is empty (no Swipe/Numbering selected)
  const hasCta = !!(data.cta && data.cta.trim());

  const heroImage = data.normies?.heroImage;
  const stats = data.stats || [];

  const handleTitleClick = (e) => {
    if (!interactive || !onElementClick) return;
    if (editingField === 'title') return;
    e.stopPropagation();
    onElementClick('header', 'title');
  };

  return (
    <>
      {/* Dark overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.18) 35%, rgba(0,0,0,0.35) 100%)',
          pointerEvents: 'none',
        }}
      />

      <NudgeableGroup groupId="content" className="absolute top-[50px] left-[52px] right-[52px] bottom-[80px]" style={{ display: 'flex', flexDirection: 'column' }}>

        {/* Header zone: hero thumbnail + kicker + title */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '28px', marginBottom: '28px' }}>
          {/* Hero thumbnail — generous size */}
          {heroImage && (
            <div style={{
              width: '140px',
              height: '140px',
              flexShrink: 0,
              background: 'rgba(0,0,0,0.35)',
              border: `2px solid ${pc.accent(0.25)}`,
              overflow: 'hidden',
            }}>
              <img
                src={heroImage}
                alt=""
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  imageRendering: 'pixelated',
                  opacity: imgLoaded ? 1 : 0,
                  transition: 'opacity 0.3s',
                }}
                onLoad={() => setImgLoaded(true)}
                crossOrigin="anonymous"
              />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Kicker */}
            {data.kickerEnabled !== false && data.kicker && (
              <div
                style={{
                  fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
                  fontSize: `${Math.max(kickerSize, 16)}px`,
                  fontWeight: 700,
                  letterSpacing: '0.18em',
                  color: pc.el.kicker || pc.textMuted,
                  textTransform: 'uppercase',
                  marginBottom: '10px',
                }}
              >
                {data.kicker}
              </div>
            )}

            {/* Title — bold and prominent */}
            <InlineEditable
              value={data.title}
              isEditing={editingField === 'title'}
              onChange={(html) => onEditChange && onEditChange('title', html)}
              onBlur={onEndEdit}
              className={`leading-[0.95] w-full${editingField === 'title' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
              style={{
                fontSize: `${Math.max(titleSize, 56)}px`,
                fontFamily: fontPreset.title,
                fontWeight: fontPreset.titleWeight || 800,
                color: pc.el.title || pc.textPrimary,
                textTransform: 'uppercase',
                letterSpacing: '-0.03em',
              }}
              onClick={handleTitleClick}
            >
              {data.title}
            </InlineEditable>

            {/* Subtitle */}
            {data.subtitleEnabled !== false && data.subtitle && (
              <div
                style={{
                  fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
                  fontSize: `${Math.max(subtitleSize, 17)}px`,
                  color: pc.el.subtitle || pc.textMuted,
                  marginTop: '10px',
                  letterSpacing: '0.03em',
                }}
              >
                {data.subtitle}
              </div>
            )}
          </div>
        </div>

        {/* Stats grid — bold numbers, generous cards */}
        {stats.length > 0 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
            {/* Top row — first 2 stats large */}
            {stats.length >= 2 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {stats.slice(0, 2).map((stat, i) => (
                  <DashboardStat key={i} label={stat.label} value={stat.value} pc={pc} size="large" fontSize={statsSize} />
                ))}
              </div>
            )}
            {/* Single stat — full width large */}
            {stats.length === 1 && (
              <DashboardStat label={stats[0].label} value={stats[0].value} pc={pc} size="large" fontSize={statsSize} />
            )}
            {/* Middle stats — 3-col grid */}
            {stats.length > 2 && stats.length <= 5 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                {stats.slice(2).map((stat, i) => (
                  <DashboardStat key={i + 2} label={stat.label} value={stat.value} pc={pc} fontSize={statsSize} />
                ))}
              </div>
            )}
            {/* Many stats — two rows of 3 */}
            {stats.length > 5 && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {stats.slice(2, 5).map((stat, i) => (
                    <DashboardStat key={i + 2} label={stat.label} value={stat.value} pc={pc} fontSize={statsSize} />
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {stats.slice(5, 8).map((stat, i) => (
                    <DashboardStat key={i + 5} label={stat.label} value={stat.value} pc={pc} fontSize={statsSize} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Tags — bottom of content zone */}
        {data.tagsEnabled !== false && data.meta && data.meta.length > 0 && (
          <div className={`flex flex-wrap items-center gap-3 mt-4${hasCta ? ' mb-6' : ''}`}>
            {/* None mode: website address sits with the tags */}
            {!hasCta && data.footerEnabled !== false && (
              <span style={{ fontSize: '22px', color: pc.el.footer, marginRight: 'auto' }}>
                {data.ctaButton ?? 'magicforest.land'}
              </span>
            )}
            {/* Push tags right when no website in row */}
            {hasCta && <span style={{ flex: 1 }} />}
            {data.meta.map((tag, i) => (
              <span
                key={i}
                style={{
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: `${Math.max(tagsSize, 12)}px`,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  color: pc.el.tagText || pc.textMuted,
                  border: `1px solid ${pc.el.tagBorder || pc.accent(0.30)}`,
                  padding: '5px 14px',
                  textTransform: 'uppercase',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </NudgeableGroup>

      {/* Footer separator + footer — only when CTA text is set (Swipe/Numbering) */}
      {hasCta && (
        <div className="absolute bottom-[36px] left-[52px] right-[52px]">
          <div style={{
            height: '1px',
            marginBottom: '14px',
            background: 'rgba(255,255,255,0.18)',
          }} />
          <SlideFooter data={data} pc={pc} />
        </div>
      )}
    </>
  );
}
